import { fg } from '@atlaskit/platform-feature-flags';

import { getBscGlobalState, sendPackageOperationalEvent } from '../../common/utils';
import {
	getItemCategory,
	isProcessingAllowedByPreferences,
	isProcessingAllowedByPreferencesWithCategory,
} from '../../common/utils/item-processing';
import { Logger } from '../../common/utils/logger';
import { CategoryStorageType, PreferenceCategory } from '../../types';
import { ItemKeyCache } from '../item-key-cache-service';

import { createIDBOpenDBRequestWrapper } from './idb-open-request-wrapper';
import type { AtlIDBOpenDBRequest, AtlIndexedDbConfig } from './types';
import { checkDBAvailability, isValidDBName, isValidVersion } from './utils';

/**
 * Checks to see if the given database name is allowed by user consent preferences.
 *
 * @param dbName the database name
 * @returns true if allowed, false if blocked
 */
export const isIndexedDbDatabaseAllowed = async (dbName: string): Promise<boolean> => {
	const availability = checkDBAvailability();

	if (availability.bypassImplementation) {
		// Fallback to allowing if it bypassed for any reason (support disabled, killswitch, etc)
		return true;
	}

	let isAllowed: boolean;
	let resolvedItemCategory: PreferenceCategory | null | undefined;

	if (fg('platform_bsc_add_category_to_idb_open_events')) {
		({ isAllowed, itemCategory: resolvedItemCategory } =
			await isProcessingAllowedByPreferencesWithCategory({
				itemKey: dbName,
				storageType: CategoryStorageType.IndexedDB,
			}));
	} else {
		isAllowed = await isProcessingAllowedByPreferences({
			itemKey: dbName,
			storageType: CategoryStorageType.IndexedDB,
		});
	}

	const categoryAttribute =
		resolvedItemCategory !== undefined
			? { itemCategory: (resolvedItemCategory ?? PreferenceCategory.Unknown) as string }
			: {};

	sendPackageOperationalEvent({
		action: 'usedAtlIndexedDBOpen',
		attributes: {
			databaseName: dbName,
			wasRejected: !isAllowed,
			...categoryAttribute,
		},
	});

	return isAllowed;
};

/**
 * AtlIndexedDb is a wrapper around the IDBFactory interface that provides
 * additional checks before opening the database, such as:
 * - SSR environment detection
 * - Feature flag checks
 * - Input validation
 * - Error logging
 *
 * When bypass mode is active, this wrapper skips special validation and delegates
 * directly to the configured IDBFactory, allowing the application to fall back to default
 * browser behavior.
 *
 * This class implements the IDBFactory interface and delegates all methods to the wrapped
 * IDBFactory instance.
 */
class AtlIndexedDb implements IDBFactory {
	private config: Required<AtlIndexedDbConfig>;
	private isAvailable: boolean;
	private bypassImplementation: boolean;

	constructor(config?: AtlIndexedDbConfig) {
		this.config = {
			enableSSRCheck: config?.enableSSRCheck ?? true,
			enableFeatureFlagCheck: config?.enableFeatureFlagCheck ?? true,
			noOpWhenUnavailable: config?.noOpWhenUnavailable ?? false,
			idbFactory: config?.idbFactory ?? globalThis.indexedDB,
		};

		// Perform initial availability check
		const availabilityCheck = checkDBAvailability(
			this.config.enableSSRCheck,
			this.config.enableFeatureFlagCheck,
		);
		this.isAvailable = availabilityCheck.available;
		this.bypassImplementation = availabilityCheck.bypassImplementation ?? false;

		if (!this.isAvailable) {
			if (this.config.noOpWhenUnavailable) {
				Logger.debug(`AtlIndexedDb unavailable (no-op mode): ${availabilityCheck.reason}`);
			} else {
				Logger.warn(`AtlIndexedDb initialization warning: ${availabilityCheck.reason}`);
			}
		} else if (this.bypassImplementation) {
			Logger.debug(`AtlIndexedDb bypassing implementation: ${availabilityCheck.reason}`);
		}
	}

	getIsAvailable(): boolean {
		return this.isAvailable;
	}

	getIsBypassingImplementation(): boolean {
		return this.bypassImplementation;
	}

	/**
	 * Opens a database connection with additional validation and checks
	 * @param dbName - The name of the database to open
	 * @param version - Optional version number for the database
	 * @returns An IDBOpenDBRequest (use with event handlers or wrap in Promise for async/await)
	 * @throws Error if database name is invalid, IndexedDB is not available (unless bypass mode is active), or the wrapped IDBFactory throws an error
	 *
	 * When bypass mode is active, validation is skipped and the wrapped IDBFactory is used directly.
	 */
	open(dbName: string, version?: number): AtlIDBOpenDBRequest {
		Logger.debug(`IndexedDB operation: open on database \"${dbName}\"`, {
			version: String(version ?? 'unspecified'),
		});

		// If bypass mode is active, skip implementation and use wrapped IDBFactory directly
		if (this.bypassImplementation) {
			return this.config.idbFactory.open(dbName, version) as AtlIDBOpenDBRequest;
		}

		if (!isValidDBName(dbName)) {
			const error = new Error('Invalid database name: must be a non-empty string');
			Logger.errorWithOperationalEvent({
				action: 'usedAtlIndexedDBOpenError',
				attributes: { databaseName: dbName },
				message: `IndexedDB open failed: ${error.message}`,
			});
			throw error;
		}

		if (!isValidVersion(version)) {
			const error = new Error('Invalid version: must be a positive integer or undefined');
			Logger.errorWithOperationalEvent({
				action: 'usedAtlIndexedDBOpenError',
				attributes: { databaseName: dbName },
				message: `IndexedDB open failed: ${error.message}`,
			});
			throw error;
		}

		const availabilityCheck = checkDBAvailability(
			this.config.enableSSRCheck,
			this.config.enableFeatureFlagCheck,
		);
		if (!availabilityCheck.available) {
			if (this.config.noOpWhenUnavailable) {
				// In some runtimes (SSR/service worker), consumers still want to import a shared
				// instance without having to guard every call.
				return createIDBOpenDBRequestWrapper();
			}
			const error = new Error(
				`Cannot open database: ${availabilityCheck.reason || 'IndexedDB is not available'}`,
			);
			Logger.errorWithOperationalEvent({
				action: 'usedAtlIndexedDBOpenError',
				attributes: { databaseName: dbName },
				message: `IndexedDB open failed: ${error.message}`,
			});
			throw error;
		}

		const requestWrapper = createIDBOpenDBRequestWrapper();
		const globalState = getBscGlobalState();
		const productName = globalState.product;
		const disableIndexedDbConsentChecks = globalState.disableIndexedDbConsentChecks ?? false;

		if (disableIndexedDbConsentChecks) {
			// Consent checks are disabled for rollout testing — send analytics but always allow
			if (fg('platform_bsc_add_category_to_idb_open_events')) {
				getItemCategory(dbName, CategoryStorageType.IndexedDB, productName)
					.then((itemCategory) => {
						sendPackageOperationalEvent({
							action: 'usedAtlIndexedDBOpen',
							attributes: {
								databaseName: dbName,
								wasRejected: false,
								consentChecksDisabled: true,
								itemCategory: (itemCategory ?? PreferenceCategory.Unknown) as string,
							},
						});
					})
					.catch((e) => {
						const errorMessage = e instanceof Error ? e.message : String(e);
						Logger.errorWithOperationalEvent({
							action: 'usedAtlIndexedDBOpenError',
							attributes: { databaseName: dbName },
							message: `IndexedDB open failed: ${errorMessage}`,
						});
					});
			} else {
				sendPackageOperationalEvent({
					action: 'usedAtlIndexedDBOpen',
					attributes: {
						databaseName: dbName,
						wasRejected: false,
						consentChecksDisabled: true,
					},
				});
			}
			ItemKeyCache.markSetByPackage(CategoryStorageType.IndexedDB, dbName);
			const actualRequest = this.config.idbFactory.open(dbName, version);
			requestWrapper.wrapRequest(actualRequest);
			return requestWrapper;
		}

		(fg('platform_bsc_add_category_to_idb_open_events')
			? isProcessingAllowedByPreferencesWithCategory({
					itemKey: dbName,
					storageType: CategoryStorageType.IndexedDB,
					productName,
				}).then(({ isAllowed, itemCategory }) => {
					const categoryAttribute = {
						itemCategory: (itemCategory ?? PreferenceCategory.Unknown) as string,
					};

					if (isAllowed) {
						ItemKeyCache.markSetByPackage(CategoryStorageType.IndexedDB, dbName);
						sendPackageOperationalEvent({
							action: 'usedAtlIndexedDBOpen',
							attributes: {
								databaseName: dbName,
								wasRejected: false,
								...categoryAttribute,
							},
						});
						const actualRequest = this.config.idbFactory.open(dbName, version);

						// Wrap the actual request to forward all events
						requestWrapper.wrapRequest(actualRequest);
					} else {
						sendPackageOperationalEvent({
							action: 'usedAtlIndexedDBOpen',
							attributes: {
								databaseName: dbName,
								wasRejected: true,
								...categoryAttribute,
							},
						});
						const blockedEvent = new Event('storageItemBlocked');
						requestWrapper.dispatchEvent(blockedEvent);
					}
				})
			: isProcessingAllowedByPreferences({
					itemKey: dbName,
					storageType: CategoryStorageType.IndexedDB,
					productName,
					allowedCallback: () => {
						ItemKeyCache.markSetByPackage(CategoryStorageType.IndexedDB, dbName);
						sendPackageOperationalEvent({
							action: 'usedAtlIndexedDBOpen',
							attributes: {
								databaseName: dbName,
								wasRejected: false,
							},
						});
						const actualRequest = this.config.idbFactory.open(dbName, version);

						// Wrap the actual request to forward all events
						requestWrapper.wrapRequest(actualRequest);
					},
					blockedCallback: () => {
						sendPackageOperationalEvent({
							action: 'usedAtlIndexedDBOpen',
							attributes: {
								databaseName: dbName,
								wasRejected: true,
							},
						});
						const blockedEvent = new Event('storageItemBlocked');
						requestWrapper.dispatchEvent(blockedEvent);
					},
				}).then(() => {
					// Handled by callbacks above
				})
		).catch((e) => {
			const errorMessage = e instanceof Error ? e.message : String(e);
			Logger.errorWithOperationalEvent({
				action: 'usedAtlIndexedDBOpenError',
				attributes: { databaseName: dbName },
				message: `IndexedDB open failed during preference check: ${errorMessage}`,
			});
			const errorEvent = new ErrorEvent('error', { message: errorMessage });
			requestWrapper.dispatchEvent(errorEvent);
		});

		return requestWrapper;
	}

	/**
	 * Opens a database connection without checking user consent preferences.
	 * This method should ONLY be used for strictly necessary databases that are critical
	 * for application functionality and do not require user consent.
	 *
	 * @param dbName - The name of the database to open
	 * @param version - Optional version number for the database
	 * @returns An IDBOpenDBRequest
	 * @throws Error if database name is invalid, IndexedDB is not available, or the wrapped IDBFactory throws an error
	 *
	 * This method performs validation and availability checks but bypasses consent enforcement.
	 */
	openStrictlyNecessary(dbName: string, version?: number): IDBOpenDBRequest {
		Logger.debug(`IndexedDB operation: openStrictlyNecessary on database \"${dbName}\"`, {
			version: String(version ?? 'unspecified'),
		});

		if (!isValidDBName(dbName)) {
			const error = new Error('Invalid database name: must be a non-empty string');
			Logger.errorWithOperationalEvent({
				action: 'usedAtlIndexedDBOpenStrictlyNecessaryError',
				attributes: { databaseName: dbName },
				message: `IndexedDB openStrictlyNecessary failed: ${error.message}`,
			});
			throw error;
		}

		if (!isValidVersion(version)) {
			const error = new Error('Invalid version: must be a positive integer or undefined');
			Logger.errorWithOperationalEvent({
				action: 'usedAtlIndexedDBOpenStrictlyNecessaryError',
				attributes: { databaseName: dbName },
				message: `IndexedDB openStrictlyNecessary failed: ${error.message}`,
			});
			throw error;
		}

		const availabilityCheck = checkDBAvailability(
			this.config.enableSSRCheck,
			this.config.enableFeatureFlagCheck,
		);
		if (!availabilityCheck.available) {
			if (this.config.noOpWhenUnavailable) {
				return createIDBOpenDBRequestWrapper();
			}
			const error = new Error(
				`Cannot open database: ${availabilityCheck.reason || 'IndexedDB is not available'}`,
			);
			Logger.errorWithOperationalEvent({
				action: 'usedAtlIndexedDBOpenStrictlyNecessaryError',
				attributes: { databaseName: dbName },
				message: `IndexedDB openStrictlyNecessary failed: ${error.message}`,
			});
			throw error;
		}

		try {
			sendPackageOperationalEvent({
				action: 'usedAtlIndexedDBOpenStrictlyNecessary',
				attributes: {
					databaseName: dbName,
				},
			});
		} catch (e) {
			// Don't fail the operation if event sending fails
			const errorMessage = e instanceof Error ? e.message : String(e);
			Logger.warn(`Failed to send operational event for openStrictlyNecessary: ${errorMessage}`);
		}

		return this.config.idbFactory.open(dbName, version);
	}

	/**
	 * Deletes a database by delegating directly to the wrapped IDBFactory instance.
	 * @param dbName - The name of the database to delete
	 * @returns An IDBOpenDBRequest
	 */
	deleteDatabase(dbName: string): IDBOpenDBRequest {
		if (!this.getIsAvailable() && this.config.noOpWhenUnavailable) {
			return createIDBOpenDBRequestWrapper();
		}
		return this.config.idbFactory.deleteDatabase(dbName);
	}

	/**
	 * Gets a list of available databases by delegating directly to the wrapped IDBFactory instance.
	 * @returns A promise that resolves to a list of database info objects
	 */
	async databases(): Promise<IDBDatabaseInfo[]> {
		if (
			this.config.idbFactory.databases &&
			typeof this.config.idbFactory.databases === 'function'
		) {
			return await this.config.idbFactory.databases();
		}
		return [];
	}

	/**
	 * Compares two values for use as IndexedDB keys by delegating to the wrapped IDBFactory instance.
	 * @param first - The first value to compare
	 * @param second - The second value to compare
	 * @returns A negative, zero, or positive value depending on the comparison result
	 */
	cmp(first: any, second: any): number {
		return this.config.idbFactory.cmp(first, second);
	}
}

const atlIndexedDb: AtlIndexedDb = new AtlIndexedDb({ noOpWhenUnavailable: true });

export { atlIndexedDb, AtlIndexedDb };
export type { AtlIndexedDbConfig, DbAvailabilityResult } from './types';
export type AtlIndexedDbFactory = IDBFactory;
export { checkDBAvailability, isValidDBName, isValidVersion } from './utils';
